<template>
  <div class="input">
    <div
      class="field"
      :class="[
                {
                    isLabelAtop,
                    hasLabel: label,
                    isRequired: required,
                    isDisabled: disabled
                },
                statusClass
            ]"
    >
      <label :for="inputId" v-if="label">{{ label }}</label>
      <input
        ref="inputElement"
        :name="inputName"
        :id="inputId"
        @focus="isFocused = true"
        @blur="
                    isFocused = false;
                    $emit('blur');
                "
        @input="$emit('update:modelValue', $event.target.value)"
        :type="type"
        :value="modelValue"
        :required="required"
        :placeholder="placeholder"
        :disabled="disabled"
        :pattern="pattern"
        :minlength="minlength"
        :maxlength="maxlength"
      />
      <transition name="trans-icon">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" v-bind:svg-inline="''" v-bind:class="'icon'" v-if="status === 'ok'" v-bind:key="'icon-ok'" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2m-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" v-bind:svg-inline="''" v-bind:class="'icon'" v-else-if="status === 'warning'" v-bind:key="'icon-warning'" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M13 14h-2v-4h2m0 8h-2v-2h2M1 21h22L12 2 1 21z"/></svg>
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" v-bind:svg-inline="''" v-bind:class="'icon'" v-else-if="status === 'error'" v-bind:key="'icon-error'" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M13 13h-2V7h2m-1 10.3a1.3 1.3 0 01-1.3-1.3 1.3 1.3 0 011.3-1.3 1.3 1.3 0 011.3 1.3 1.3 1.3 0 01-1.3 1.3M15.73 3H8.27L3 8.27v7.46L8.27 21h7.46L21 15.73V8.27L15.73 3z"/></svg>
      </transition>
    </div>
    <transition name="trans-message">
      <div v-if="message" :class="{ error: statusClass === 'isError' }">{{ message }}</div>
    </transition>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";

export default defineComponent({
  props: {
    type: {
      type: String,
      default: "text",
      validator(val: string) {
        return ["text", "password", "email", "tel"].includes(val);
      },
    },
    label: {
      type: String,
    },
    name: {
      type: String,
    },
    id: {
      type: String,
    },
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
    },
    message: {
      type: String,
    },
    pattern: {
      type: String,
    },
    minlength: {
      type: Number,
    },
    maxlength: {
      type: Number,
    },
    status: {
      type: String,
      default: "",
      validator(val: string) {
        return ["", "ok", "warning", "error"].includes(val);
      },
    },
    modelValue: {
      type: String,
    },
  },
  data() {
    return {
      isFocused: false,
      inputId: "",
      inputName: "",
    };
  },
  created() {
    this.inputId =
      this.id ||
      "id" + window.crypto.getRandomValues(new Uint32Array(1)).toString();
    this.inputName = this.name || this.inputId;
  },
  computed: {
    isLabelAtop(): boolean {
      return this.isFocused || !!this.modelValue;
    },
    statusClass(): string {
      return this.status
        ? "is" + this.status[0].toUpperCase() + this.status.substr(1)
        : "";
    },
    validity(): object {
      const inputElement = this.$refs.inputElement as HTMLFormElement;
      return inputElement.validity;
    },
  },
});
</script>

<style lang="scss" scoped>
.input {
  position: relative;
  width: 100%;
  color: $color-secondary-darkgray;
  overflow: hidden;
}

.field {
  height: 52px;
  width: 100%;

  &.hasLabel {
    input {
      padding-top: 10px;
    }
    &.isLabelAtop {
      label {
        transform: translateY(-12px) scale(0.7);
      }
    }
    &:not(.isLabelAtop) {
      input::placeholder {
        color: transparent;
        transition: color 0.2s ease;
      }
    }
  }

  &.isRequired {
    &::after {
      content: "";
      display: block;
      position: absolute;
      right: 11px;
      top: 9px;
      width: 6px;
      height: 6px;
      border-radius: 6px;
      background-color: $color-secondary-darkgray;
      pointer-events: none;
      transition: all 0.2s ease;
    }
    &.isOk,
    &.isWarning,
    &.isError {
      &::after {
        right: 10px;
        top: 10px;
      }
    }
  }

  &.isDisabled {
    label {
      cursor: not-allowed;
      color: $color-secondary-bluegray-50;
    }
    input {
      cursor: not-allowed;
      border-bottom-color: $color-secondary-bluegray-50;
      &:hover {
        background-color: #f6f6f6;
      }
      &::placeholder {
        color: $color-secondary-bluegray-50;
      }
    }
  }

  &.isRequired,
  &.isOk,
  &.isWarning,
  &.isError {
    input {
      padding-right: 46px;
    }
  }

  &.isOk {
    input {
      color: $text-color;
      background-color: $color-status-ok-faded;
      border-color: $ok-color;
      outline: 1px $ok-color;
      &:focus {
        border-color: $ok-color;
      }
    }
    .icon {
      fill: $ok-color;
    }
  }

  &.isWarning {
    input {
      background-color: $color-status-warning-faded;
      border-bottom-color: $color-status-warning;
      &:focus {
        border-color: $color-status-warning;
      }
    }
    .icon {
      fill: $color-status-warning;
    }
  }

  &.isError {
    input {
      outline: 1px $error-color;
      color:$text-color;
      background-color: $color-status-error-faded;
      border-color: $error-color;
      &:focus {
        border-color: $error-color;
      }
    }
    .icon {
      fill: $error-color;
    }
  }
}

input {
  outline: 1px $color-secondary-darkgray;
  color:$text-color;
  font-size: inherit;
  outline: none;
  height: 100%;
  width: 100%;
  padding-left: 20px;
  padding-right: 20px;
  border: 1px solid  $color-primary-gray; 
  background-color: $background-gray;
  
  &::placeholder {
    color: $color-primary-gray;
    transition: color 0.2s ease 0.15s;
  }

  &:hover {
    color: $text-color;
    background-color: $color-field-backdrop;
    border: 1px solid $color-primary-blue;
    outline: 1px $color-primary-blue;
  }

  &:focus {
    background-color: $color-field-backdrop;
    border: 2px solid $color-primary-blue;
    color:$text-color;
    outline: 2px $color-primary-blue;
  }
}

label {
  position: absolute;
  top: 13px;
  left: 20px;
  transition: transform 0.2s ease;
  transform-origin: left center;
  pointer-events: none;
  white-space: nowrap;
}

.message {
  font-size: 12px;
  line-height: 24px;
  margin-left: 1px;
  color: $color-secondary-darkgray;
}

.icon {
  pointer-events: none;
  position: absolute;
  top: 14px;
  right: 14px;
  outline: none;
}

.trans-icon-enter-active,
.trans-icon-leave-active,
.trans-message-enter-active,
.trans-message-leave-active {
  transition: all 0.2s ease;
}

.trans-message-enter-active,
.trans-message-leave-active {
  overflow: hidden;
}

.trans-icon-enter,
.trans-icon-leave-to,
.trans-message-enter,
.trans-message-leave-to {
  opacity: 0;
}

.trans-icon-enter {
  transform: translateY(-10px);
}
.trans-icon-leave-to {
  transform: translateY(10px);
}

.trans-message-enter,
.trans-message-leave-to {
  max-height: 0;
}
.trans-message-enter-to,
.trans-message-leave {
  max-height: 24px;
}

.error {
  font-weight: bold;
  color: $error-color;
}
</style>
